<script setup>
/*==================================================================*/
/*================== VUE IMPORTS ===================================*/
/*==================================================================*/

import { ref, computed, onMounted } from 'vue';

/*==================================================================*/
/*================== IMPORTS DOS COMPONENTES =======================*/
/*==================================================================*/

import TelaTabela from '@/components/v2/telas/TelaTabela/TelaTabela.vue';
import BotaoTextoBase from '@/components/v2/base/BotaoTextoBase/BotaoTextoBase.vue';
import CampoAutocompleteBase from '@/components/v2/base/CampoAutocompleteBase/CampoAutocompleteBase.vue';
import ModalConfirmacao from '@/components/v2/modais/ModalConfirmacao/ModalConfirmacao.vue';

/*==================================================================*/
/*================== IMPORTS DE SERVICES ===========================*/
/*==================================================================*/

import JobAlterarCoordenadorService from '@/api/v2/services/JobAlterarCoordenadorService.js';

/*==================================================================*/
/*================== CONTROLES DESATIVADOS =========================*/
/*==================================================================*/

const disable = ref(false);

const alteraDisable = (update = false) => {

    disable.value = update;

};

/*==================================================================*/
/*================== DADOS DE JOBS =================================*/
/*==================================================================*/

const jobs = ref([]);
const dados = ref([]);

/*==================================================================*/
/*================== DADOS DE COORDENADORES DE CONTRATOS ==========*/
/*==================================================================*/

const coordenadoresDeContratos = ref([]);

/*==================================================================*/
/*================== NOVO COORDENADOR SELECIONADO ===================*/
/*==================================================================*/

const novoCoordenadorDeContratos = ref({});

const alteraNovoCoordenadorDeContratos = (coordenador = {}) => {

    novoCoordenadorDeContratos.value = coordenador;

};

/*==================================================================*/
/*================== JOBS SELECIONADOS NA GRID ======================*/
/*==================================================================*/

const selecionados = ref([]);

const alteraSelecionados = (dados = []) => {

    selecionados.value = dados;

};

/*==================================================================*/
/*================== PREPARA DADOS =================================*/
/*==================================================================*/

const preparaDados = (dadosParaPreparar = []) => {

    dados.value = dadosParaPreparar.map((dado = {}) => ({
        id: dado.id ?? '',
        codigo: dado.codigo ?? '',
        status: dado.status ?? '',
        comercial: dado.comercial ?? {},
        gestorDeContratos: dado.gestorDeContratos ?? {},
        coordenadorDeContratos: dado.coordenadorDeContratos ?? {},
        descricao: dado.descricao ?? '',
        cliente: dado.cliente ?? {},
    }));

};

/*==================================================================*/
/*================== MÉTODOS DE LOAD DAS VIEWS =====================*/
/*==================================================================*/

const loadJobs = async () => {

    alteraDisable(true);

    const response = await JobAlterarCoordenadorService.pegaViewJobs();

    jobs.value = response?.data || [];
    preparaDados(jobs.value);

    alteraDisable(false);

};

const loadCoordenadoresDeContratos = async () => {

    const response = await JobAlterarCoordenadorService.pegaViewCoordenadoresDeContratos();

    coordenadoresDeContratos.value = response?.data || [];

};

/*==================================================================*/
/*================== FILTROS DE JOB E COORDENADOR ==================*/
/*==================================================================*/

const dadoCodigoFiltro = ref('');
const dadoCoordenadorFiltro = ref('');

const dadoCodigoFiltroValido = (codigo) => {

    if(dadoCodigoFiltro.value === ''){
        return true;
    }

    if(codigo != dadoCodigoFiltro.value){
        return false;
    }

    return true;

};

const dadoCoordenadorFiltroValido = (coordenadorNome) => {

    if(dadoCoordenadorFiltro.value === ''){
        return true;
    }

    if(coordenadorNome != dadoCoordenadorFiltro.value){
        return false;
    }

    return true;

};

/*==================================================================*/
/*================== DADOS FILTRADOS ===============================*/
/*==================================================================*/

const dadosFiltrados = computed(() => {

    return dados?.value?.reduce((acc, dado) => {

        if(!dadoCodigoFiltroValido(dado?.codigo)){
            return acc;
        }

        if(!dadoCoordenadorFiltroValido(dado?.coordenadorDeContratos?.nome)){
            return acc;
        }

        acc.push(dado);

        return acc;

    }, []);

});

/*==================================================================*/
/*================== ALTERA FILTROS ================================*/
/*==================================================================*/

const alteraDadoCodigoFiltro = (dado) => {

    dadoCodigoFiltro.value = dado?.codigo || '';

};

const alteraDadoCoordenadorFiltro = (dado) => {

    dadoCoordenadorFiltro.value = dado?.coordenadorDeContratos?.nome || '';

};

/*==================================================================*/
/*================== FILTROS CONFIGURADOS ==========================*/
/*==================================================================*/

const filtros = computed(() => [

    { label: 'Jobs',          items: dados.value, itemText: 'codigo',                     onChange: alteraDadoCodigoFiltro },
    { label: 'Coordenadores', items: dados.value, itemText: 'coordenadorDeContratos.nome', onChange: alteraDadoCoordenadorFiltro },

]);

/*==================================================================*/
/*================== HEADERS DA TABELA =============================*/
/*==================================================================*/

const dadosHeaders = [
    { text: 'N° do Job',                value: 'codigo',                        type: 'string' },
    { text: 'Status',                   value: 'status',                        type: 'string' },
    { text: 'Comercial',                value: 'comercial.nome',                type: 'string' },
    { text: 'Gestor',                   value: 'gestorDeContratos.nome',        type: 'string' },
    { text: 'Coordenador de Contratos', value: 'coordenadorDeContratos.nome',   type: 'string' },
    { text: 'Descrição',                value: 'descricao',                     type: 'string' },
    { text: 'Cliente',                  value: 'cliente.nomeFantasia',          type: 'string' },
];

/*==================================================================*/
/*================== VALIDAÇÃO DO BOTÃO ALTERAR ====================*/
/*==================================================================*/

const disabledBotaoAlterarCoordenador = computed(() => {

    if(disable.value){
        return true;
    }

    if(!novoCoordenadorDeContratos.value?.id){
        return true;
    }

    if(!selecionados.value?.length){
        return true;
    }

    return false;

});

/*==================================================================*/
/*================== MODAL DE CONFIRMAÇÃO ==========================*/
/*==================================================================*/

const modalConfirmacaoAlterarCoordenadorRef = ref(null);

/*==================================================================*/
/*================== AO ALTERAR COORDENADOR ========================*/
/*==================================================================*/

const aoAlterarCoordenador = async () => {

    if(disabledBotaoAlterarCoordenador.value){
        return;
    }

    await modalConfirmacaoAlterarCoordenadorRef.value?.abrirModal();

};

/*==================================================================*/
/*================== CONFIRMA ALTERAÇÃO DE COORDENADOR =============*/
/*==================================================================*/

const confirmaAlteracaoDeCoordenador = async () => {

    alteraDisable(true);

    const jobsIDs = selecionados.value.map((dado) => dado?.id);

    await JobAlterarCoordenadorService.alteraCoordenadorDosJobs(jobsIDs, novoCoordenadorDeContratos.value?.id);

    selecionados.value = [];
    novoCoordenadorDeContratos.value = {};

    await loadJobs();

    alteraDisable(false);

    await modalConfirmacaoAlterarCoordenadorRef.value?.fecharModal();

};

/*==================================================================*/
/*================== ON MOUNTED ====================================*/
/*==================================================================*/

onMounted(async () => {

    await loadJobs();
    await loadCoordenadoresDeContratos();

});

</script>

<template>

    <TelaTabela
        :disabled="disable"
        :filtros="filtros"
        :headers="dadosHeaders"
        :dados="dadosFiltrados"
        selecao="multiplo"
        :selecionados="selecionados"
        paginacao="paginada"
        @update:selecionados="alteraSelecionados"
    >

        <template #tela-titulo>

            Job Alterar Coordenador

        </template>

        <template #toolbar-baixo-esquerda>

            <CampoAutocompleteBase
                label="Novo Coordenador de Contratos"
                :value="novoCoordenadorDeContratos"
                :items="coordenadoresDeContratos"
                item-text="nome"
                :disabled="disable"
                return-object
                @change="alteraNovoCoordenadorDeContratos"
            ></CampoAutocompleteBase>

        </template>

        <template #toolbar-baixo-direita>

            <BotaoTextoBase
                class="c-botao-alterar-coordenador"
                :disabled="disabledBotaoAlterarCoordenador"
                :click="aoAlterarCoordenador"
            >
                Alterar Coordenador
            </BotaoTextoBase>

        </template>

        <template #modais>

            <ModalConfirmacao
                ref="modalConfirmacaoAlterarCoordenadorRef"
                max-width="600"
                :disabled="disable"
                :confirmar="confirmaAlteracaoDeCoordenador"
            >

                <template #header>
                    Alterar Coordenador
                </template>

                <template #body>
                    Deseja realmente alterar o Coordenador de Contratos dos {{ selecionados.length }} Job(s) selecionado(s) para "{{ novoCoordenadorDeContratos?.nome }}"?
                </template>

            </ModalConfirmacao>

        </template>

    </TelaTabela>

</template>

<style scoped>

.c-botao-alterar-coordenador{
    width: 100%;
}

</style>
